import { parseMessageToArray } from './tableParser';

/**
 * What rows a turn produced.
 *
 * One question, one answer, read by two places: the transcript, which shows a
 * summary card when a turn has nothing but rows, and the data rail, which
 * renders them. Two components parsing `data_sources_map` separately is two
 * answers to the same question, and they drift.
 *
 * Takes a message straight out of the chat array — no React, no props plumbing —
 * so the rail can hold `{ messageId, sourceKey }` and look the turn up rather
 * than carry a snapshot that goes stale the moment the turn re-renders.
 *
 * `null` means there is nothing to render. It does NOT mean the turn has no data
 * channel: a turn whose SQL matched no rows still keeps its Data chip (that is
 * `tabConfig.showTabs`, unchanged) and the rail shows it as empty, because "0
 * rows" is an answer a reader asked for.
 */

// Only these parse `message` for rows. A prose turn that happens to contain a
// markdown table is prose, and the rail is not for prose.
const TABULAR_DATATYPES = new Set(['sql', 'insights']);

// Turn types that ran something with rows behind it. The same set `tabConfig`
// gives a Data chip to, so the chip and this agree by construction.
const ROW_BEARING_ANSWERS = new Set(['sql', 'api', 'mixed']);

const columnsOf = (rows) => (rows.length > 0 && rows[0] ? Object.keys(rows[0]) : []);

/**
 * The row count for one output. `total_rows` arrives keyed per source from a
 * multi-step turn and as a bare number from a single-step one; when it is absent
 * the rows in hand are the count, because the session API returns up to 30 and
 * older agents send no total at all — reading 0 there would tell the pager the
 * turn found nothing.
 */
const totalFor = (totals, key, rows) => {
  if (totals && typeof totals === 'object') {
    const named = totals[key];
    return typeof named === 'number' ? named : rows.length;
  }
  return typeof totals === 'number' ? totals : rows.length;
};

export const resultSet = (message) => {
  if (!message || typeof message !== 'object') return null;

  const totals = message.total_rows;
  const map = message.data_sources_map;

  // Named outputs. The key is the backend's own — it travels to the download
  // endpoint as `source_key`, so it is never prettified; the label is positional
  // and is the only part a reader sees.
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    const keys = Object.keys(map);
    if (keys.length > 0) {
      const outputs = keys.map((key, index) => {
        const rows = parseMessageToArray(map[key], null) || [];
        return {
          key,
          label: `Output ${index + 1}`,
          rows,
          columns: columnsOf(rows),
          totalRows: totalFor(totals, key, rows),
        };
      });
      return {
        outputs,
        totalRows: totals ?? null,
        isMultiSource: outputs.length > 1,
      };
    }
  }

  // The unnamed single output: a turn that put its table straight in `message`.
  // It carries no key because the backend stored none, and the download endpoint
  // reads that same absence — inventing one here would send it a key it has
  // never seen.
  const datatype = message.datatype || message.type;
  if (!TABULAR_DATATYPES.has(datatype)) return null;

  const rows = parseMessageToArray(message.message, null) || [];
  if (rows.length === 0) {
    // Nothing inline — but a turn of this kind HAS rows somewhere. A reopened
    // session is the ordinary case: discover keeps the rows in their own column
    // and does not always replay them in the session payload, while the
    // pagination API serves them by (session, message, source) all the same.
    // So the answer is "ask", not "none": `needsFetch` tells the rail to let the
    // table load page one instead of rendering an empty panel.
    if (!ROW_BEARING_ANSWERS.has(message.answer_type)) return null;
    return {
      outputs: [{ key: null, label: 'Result', rows: [], columns: [], totalRows: 0 }],
      totalRows: null,
      isMultiSource: false,
      needsFetch: true,
    };
  }

  const total = typeof totals === 'number' ? totals : rows.length;
  return {
    outputs: [{ key: null, label: 'Result', rows, columns: columnsOf(rows), totalRows: total }],
    totalRows: total,
    isMultiSource: false,
  };
};
